/**
 * WebAudioAdapter — implements AudioAdapter for the browser.
 *
 * RX (playback) path:
 *   VOICE_PACKET (base64 RTP) → playFrame()
 *     → parseRtp → decodePcm → linear-resample 8k→ctx.sampleRate
 *     → push into a SharedArrayBuffer-free ring buffer in the
 *       playback worklet via port.postMessage
 *     → worklet's process() drains the ring into the output buffer
 *
 * TX (capture) path:
 *   getUserMedia → MediaStreamAudioSourceNode → capture worklet
 *     → worklet downsamples ctx.sampleRate → 8k by averaging chunks
 *     → emits 160-sample frames every 20ms via port.postMessage
 *     → main thread encodes µ-law, builds RTP, calls send()
 *
 * Worklet code is shipped as an inline string + Blob URL so the
 * library stays a single file with no external worklet asset to
 * deploy. Trade-off: the worklet source is duplicated in the bundle,
 * but it's < 2 KB and avoids Vite/webpack-specific worklet handling.
 *
 * Codec: defaults to µ-law (matches WalkieFleet AudioCodec=1). If
 * audio comes through as static, construct with `{ codec: "alaw" }`.
 */

import type { AudioAdapter, Base64Guid } from "../types";
import { decodePcm, encodePcm, type Codec } from "./mulaw";
import { buildRtp, parseRtp, randomSsrc } from "./rtp";

export interface WebAudioAdapterOptions {
  /** Override the codec — defaults to µ-law. Set "alaw" for European /
   *  most non-NA WalkieFleet deployments if RX sounds garbled. */
  codec?: Codec;
  /** Mic constraints. Defaults pick the AEC/NS/AGC trio that works
   *  reliably for desktop dispatch consoles with open speakers. */
  micConstraints?: MediaTrackConstraints;
  /** Optional logger (defaults to console). */
  log?: (...args: unknown[]) => void;
}

const PT_MULAW = 0;          // RFC 3551 PCMU
const PT_ALAW  = 8;          // RFC 3551 PCMA
const PT_FLEET_VOICE = 106;  // FleetConsole's dynamic RTP payload type

/**
 * The MCPTT manager wraps voice in a CUSTOM 28-byte header, NOT
 * standard RTP. The layout on the wire is:
 *
 *   [12-byte RTP-style header][16-byte CallID GUID][N bytes µ-law samples]
 *
 * `MCPTTClient.handleVoicePacket` (lib/MCPTTClient.ts) extracts the
 * CallID from bytes 12..28 and forwards the FULL buffer to us. We
 * therefore have to strip an additional 16 bytes after the standard
 * RTP header before handing the bytes to the µ-law decoder; otherwise
 * the first 16 "samples" of every frame are scrambled GUID bits.
 *
 * This number is hard-coded because the manager's wrapper is fixed
 * (the wire layout has not changed across the WalkieFleet 5.x line).
 */
const WALKIEFLEET_CALLID_PREFIX = 16;

// Inline worklet source.
//
// Two processors registered: 'mcptt-rx' (playback ring) and 'mcptt-tx'
// (capture downsampler + framer). Both read framing parameters
// (frameSizeRx / frameSizeTx) from processorOptions so the same module
// works regardless of the AudioContext's sample rate.
const WORKLET_SOURCE = /* js */`
class McpttRxProcessor extends AudioWorkletProcessor {
  constructor(opts) {
    super();
    // Generously sized ring buffer (~2s at 48 kHz). We trade memory
    // for crash-free behavior under bursty WS deliveries.
    this.ring = new Float32Array(96000);
    this.write = 0;
    this.read = 0;
    this.size = 0;
    this.port.onmessage = (e) => {
      const data = e.data;
      if (data && data.type === 'samples') {
        const arr = data.samples;
        for (let i = 0; i < arr.length; i++) {
          this.ring[this.write] = arr[i];
          this.write = (this.write + 1) % this.ring.length;
          if (this.size < this.ring.length) {
            this.size++;
          } else {
            // Overrun — drop the oldest sample to keep tail latency bounded.
            this.read = (this.read + 1) % this.ring.length;
          }
        }
      } else if (data && data.type === 'flush') {
        this.write = this.read = this.size = 0;
      }
    };
  }
  process(_inputs, outputs) {
    const ch0 = outputs[0][0];
    const want = ch0.length;
    let i = 0;
    while (i < want && this.size > 0) {
      ch0[i++] = this.ring[this.read];
      this.read = (this.read + 1) % this.ring.length;
      this.size--;
    }
    while (i < want) ch0[i++] = 0;
    return true;
  }
}
registerProcessor('mcptt-rx', McpttRxProcessor);

class McpttTxProcessor extends AudioWorkletProcessor {
  constructor(opts) {
    super();
    const o = opts.processorOptions || {};
    this.targetSampleRate = o.targetSampleRate || 8000;
    this.frameSize        = o.frameSize        || 160;
    this.ratio            = sampleRate / this.targetSampleRate;
    this.frame  = new Float32Array(this.frameSize);
    this.frameI = 0;
    // Decimation accumulator: average the next 'ratio' samples into one.
    this.accSum = 0;
    this.accCnt = 0;
    this.targetEvery = this.ratio;
  }
  process(inputs) {
    const ch0 = inputs[0] && inputs[0][0];
    if (!ch0) return true;
    for (let i = 0; i < ch0.length; i++) {
      this.accSum += ch0[i];
      this.accCnt++;
      if (this.accCnt >= this.targetEvery) {
        this.frame[this.frameI++] = this.accSum / this.accCnt;
        this.accSum = 0;
        this.accCnt = 0;
        if (this.frameI >= this.frameSize) {
          this.port.postMessage({ type: 'frame', samples: this.frame.slice() });
          this.frameI = 0;
        }
      }
    }
    return true;
  }
}
registerProcessor('mcptt-tx', McpttTxProcessor);
`;

export type AudioState = "off" | "enabling" | "ready" | "denied" | "error";

export class WebAudioAdapter implements AudioAdapter {
  private codec: Codec;
  private log:   (...args: unknown[]) => void;
  private micConstraints: MediaTrackConstraints;

  /**
   * Audio lifecycle state. The dispatcher must explicitly enable audio
   * (one-click toggle in the AppBar) because:
   *
   *   1. Browsers REQUIRE a user gesture to construct/resume an
   *      AudioContext (autoplay policy). Trying eagerly fails silently.
   *   2. We want the mic-permission prompt to appear at a predictable
   *      time, not in the middle of an active TX press.
   *
   * The state machine drives:
   *   off       → operator hasn't enabled audio; playFrame is a no-op
   *   enabling  → enable() is in flight (context init + mic preflight)
   *   ready     → RX plays + TX is armed; everything works
   *   denied    → mic permission denied; RX still works, TX disabled
   *   error     → context or worklet failed to initialize
   */
  private _state: AudioState = "off";
  state(): AudioState { return this._state; }
  /** Optional listener for state transitions. */
  onState: ((s: AudioState) => void) | null = null;
  private _lastError: string | null = null;
  lastError(): string | null { return this._lastError; }
  private setState(s: AudioState, err?: string | null): void {
    this._state = s;
    if (err !== undefined) this._lastError = err;
    try { this.onState?.(s); } catch { /* never let listeners crash us */ }
  }

  // Lazy AudioContext; created on first user-gesture-driven enable().
  private ctx: AudioContext | null = null;
  private workletReady: Promise<void> | null = null;

  // RX graph: rxNode -> rxGain -> rxAnalyser -> destination
  // The gain implements both volume control and mute (mute = gain 0,
  // restored on un-mute). The analyser is post-gain so the meter
  // reflects what the operator actually hears (muting collapses the
  // meter to 0, which is what an operator expects).
  private rxNode:     AudioWorkletNode | null = null;
  private rxGain:     GainNode | null = null;
  private rxAnalyser: AnalyserNode | null = null;
  private _volume = 1;
  private _muted  = false;
  private _volumeBeforeMute = 1;

  // TX analyser: tapped at the mic source (pre-worklet) so it reads
  // raw mic energy regardless of downsampling artefacts.
  private txAnalyser: AnalyserNode | null = null;

  // Reusable byte buffer for analyser reads — avoid allocating in the
  // 30Hz meter loop.
  private analyserScratch = new Uint8Array(256);

  // TX
  private mediaStream: MediaStream | null = null;
  private srcNode:     MediaStreamAudioSourceNode | null = null;
  private txNode:      AudioWorkletNode | null = null;
  private txSeq = 0;
  private txTs  = 0;
  private txSsrcOverride: number | null = null;

  // Sample-rate gymnastics for RX resample.
  private rxLastSample = 0;
  private rxSourceSampleRate = 8000;

  // One-shot debug logger: dump the first VOICE_PACKET we ever see
  // so we can verify on the wire what the manager is actually sending
  // (codec, payload type, length). Cleared after the first frame.
  private debuggedFirstFrame = false;

  constructor(opts: WebAudioAdapterOptions = {}) {
    this.codec = opts.codec ?? "mulaw";
    this.log   = opts.log ?? ((..._a: unknown[]) => {});
    this.micConstraints = opts.micConstraints ?? {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl:  true,
      channelCount:     1,
    };
  }

  /** Switch codec at runtime. Useful if RX sounds wrong. */
  setCodec(codec: Codec): void { this.codec = codec; }
  getCodec(): Codec { return this.codec; }

  /** Server-advertised audio sample rate for incoming VOICE_PACKET
   *  payloads. Fleet Server 5.8 reports 16000 Hz; hardcoding 8000 Hz
   *  halves playback speed / pitch. */
  setSourceSampleRate(rate: number): void {
    if (Number.isFinite(rate) && rate > 0) this.rxSourceSampleRate = rate;
  }
  getSourceSampleRate(): number { return this.rxSourceSampleRate; }

  /**
   * Operator-driven enable. MUST be called from a user gesture
   * handler (click, keypress) or AudioContext construction will fail
   * silently. After this resolves with state="ready", RX plays
   * automatically and TX is armed.
   *
   * Mic permission is preflighted here (track briefly opened then
   * stopped) so subsequent startPTT() doesn't pop a permission prompt
   * mid-press — that's a horrible PTT experience.
   */
  async enable(): Promise<void> {
    if (this._state === "ready" || this._state === "enabling") return;
    this.setState("enabling", null);
    try {
      await this.ensureContext();
      await this.ensureRxNode();
      // Preflight mic permission. We don't keep the stream — start()
      // will reopen its own when TX actually fires. This just gets
      // the prompt out of the way and warms the device.
      try {
        const s = await navigator.mediaDevices.getUserMedia({ audio: this.micConstraints, video: false });
        for (const t of s.getTracks()) t.stop();
      } catch (e) {
        // RX still works without a mic. Mark denied so the UI can
        // surface a helpful "TX disabled — grant mic to transmit" hint.
        this.log("mic preflight failed:", e);
        this.setState("denied", (e as Error).message ?? String(e));
        return;
      }
      this.setState("ready", null);
    } catch (e) {
      this.log("audio enable failed:", e);
      this.setState("error", (e as Error).message ?? String(e));
      throw e;
    }
  }

  async disable(): Promise<void> {
    await this.close();
    this.setState("off", null);
  }

  /** True iff RX playback is permitted right now. */
  private rxAllowed(): boolean {
    return this._state === "ready" || this._state === "denied";
    // ↑ "denied" still allows playback; only TX is gated.
  }

  /** True iff TX (mic) is permitted right now. */
  txAllowed(): boolean { return this._state === "ready"; }

  // ============================================================
  // Lifecycle plumbing
  // ============================================================

  /** Create or reuse the shared AudioContext + worklet module. */
  private async ensureContext(): Promise<void> {
    if (this.ctx) {
      if (this.ctx.state === "suspended") {
        try { await this.ctx.resume(); } catch { /* operator must click */ }
      }
      return;
    }
    const Ctx = (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext);
    this.ctx = new Ctx({ latencyHint: "interactive" });
    const blob = new Blob([WORKLET_SOURCE], { type: "application/javascript" });
    const url  = URL.createObjectURL(blob);
    this.workletReady = this.ctx.audioWorklet.addModule(url).finally(() => {
      URL.revokeObjectURL(url);
    });
    await this.workletReady;
  }

  /** Build (or reuse) the playback worklet, gain, and analyser; wire
   *  them to the destination as `worklet -> gain -> analyser -> dest`. */
  private async ensureRxNode(): Promise<AudioWorkletNode> {
    await this.ensureContext();
    if (!this.ctx) throw new Error("AudioContext failed to initialize");
    if (this.rxNode) return this.rxNode;
    const node = new AudioWorkletNode(this.ctx, "mcptt-rx", {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [1],
    });
    const gain = this.ctx.createGain();
    gain.gain.value = this._muted ? 0 : this._volume;
    const analyser = this.ctx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.4;
    node.connect(gain).connect(analyser).connect(this.ctx.destination);
    this.rxNode = node;
    this.rxGain = gain;
    this.rxAnalyser = analyser;
    return node;
  }

  // ============================================================
  // RX: playFrame — called by MCPTTClient on every VOICE_PACKET
  // ============================================================

  playFrame(rtpFrame: Uint8Array, _callId: Base64Guid): void {
    // Off / enabling / error → silently drop. The dispatcher must
    // click "Enable Audio" first; this isn't an error, it's the
    // intended autoplay-safe default.
    if (!this.rxAllowed()) return;
    if (!this.ctx || !this.rxNode) {
      // Defensive — should be impossible if state is "ready" / "denied"
      // since enable() awaits ensureRxNode(). Bail rather than crash.
      return;
    }
    const parsed = parseRtp(rtpFrame);
    if (!parsed) return;
    // After the standard 12-byte RTP header, strip the 16-byte CallID
    // GUID that the manager prepends. See WALKIEFLEET_CALLID_PREFIX.
    if (parsed.payload.length < WALKIEFLEET_CALLID_PREFIX) return;
    const audioBytes = parsed.payload.subarray(WALKIEFLEET_CALLID_PREFIX);
    if (audioBytes.length === 0) return;

    // RFC 3551 payload-type discrimination: PT=0 is µ-law, PT=8 is
    // a-law. If the manager sends one of those we trust it over the
    // operator's UI toggle (the toggle exists for codec=1 deployments
    // where PT is non-discriminating).
    const codec: Codec = parsed.header.payloadType === PT_ALAW
      ? "alaw"
      : parsed.header.payloadType === PT_MULAW
        ? "mulaw"
        : this.codec;

    if (!this.debuggedFirstFrame) {
      this.debuggedFirstFrame = true;
      this.log("first VOICE_PACKET decoded", {
        payloadType:    parsed.header.payloadType,
        sequence:       parsed.header.sequence,
        timestamp:      parsed.header.timestamp,
        ssrc:           parsed.header.ssrc,
        wrappedLen:     rtpFrame.length,
        rtpPayloadLen:  parsed.payload.length,
        audioByteLen:   audioBytes.length,
        codec,
        ctxSampleRate:  this.ctx.sampleRate,
        sourceSampleRate: this.rxSourceSampleRate,
      });
    }

    const samples8k = decodePcm(audioBytes, codec);
    const samplesCtx = this.resampleUp(samples8k, this.ctx.sampleRate);
    this.rxNode.port.postMessage({ type: "samples", samples: samplesCtx }, [samplesCtx.buffer]);
  }

  /** Linear interpolation sourceSampleRate → ctx.sampleRate. Quality is
   *  dispatcher-grade voice — high-fidelity is not the goal.
   *
   *  Convention: srcIdx = i / ratio. That value is "where in the
   *  source array does this output sample fall". `floor(srcIdx)` is
   *  the sample at-or-before; `floor(srcIdx) + 1` is the sample
   *  after. Interp blends the two by `t = srcIdx - floor(srcIdx)`.
   *
   *  Frame-boundary continuity: at i0 = 0, we use samples[0] (NOT the
   *  prev frame's tail — that would shift the whole output 1 sample
   *  early, the bug we just removed). The last-sample carryover is
   *  only consulted if a hypothetical srcIdx ever lands BEFORE
   *  samples[0], which it can't here (srcIdx >= 0). Kept for paranoia
   *  on future variable-rate inputs. */
  private resampleUp(samples: Float32Array, targetRate: number): Float32Array {
    const ratio = targetRate / this.rxSourceSampleRate;
    const out = new Float32Array(Math.floor(samples.length * ratio));
    const last = this.rxLastSample;
    for (let i = 0; i < out.length; i++) {
      const srcIdx = i / ratio;
      const i0 = Math.floor(srcIdx);
      const t  = srcIdx - i0;
      const a = samples[i0]     ?? last;
      const b = samples[i0 + 1] ?? a;
      out[i] = a * (1 - t) + b * t;
    }
    if (samples.length > 0) this.rxLastSample = samples[samples.length - 1];
    return out;
  }

  /** Drop everything currently queued for playback. */
  flushRx(): void {
    this.rxNode?.port.postMessage({ type: "flush" });
  }

  // ============================================================
  // TX: start / stop — called by MCPTTClient.startPTT() / stopPTT()
  // ============================================================

  async start(
    send: (rtpFrame: Uint8Array) => void,
    opts: { sampleRate: number; frameSizeMs: number; codec: number; ssrc: number },
  ): Promise<void> {
    // TX is gated on a successful enable() with mic permission. If the
    // operator hasn't enabled audio, or has but denied the mic, swallow
    // the start request — the visual TX path still runs. This avoids
    // crashing the spacebar handler when audio is intentionally off.
    if (!this.txAllowed()) {
      this.log("audio TX skipped \u2014 state:", this._state);
      return;
    }

    // The manager's `frameSizeMs` is actually advertised in samples
    // (poorly named upstream). At codec=1 / 8 kHz that's ~160 = 20 ms.
    // We accept either interpretation: > 200 is samples, otherwise ms.
    const frameSize = opts.frameSizeMs > 200
      ? opts.frameSizeMs
      : Math.round((opts.frameSizeMs * opts.sampleRate) / 1000);

    await this.ensureContext();
    if (!this.ctx) throw new Error("AudioContext failed to initialize");
    this.txSsrcOverride = opts.ssrc;
    this.txSeq = (Math.random() * 0xffff) >>> 0;
    this.txTs  = (Math.random() * 0xffffffff) >>> 0;

    const stream = await navigator.mediaDevices.getUserMedia({ audio: this.micConstraints, video: false });
    this.mediaStream = stream;
    this.srcNode = this.ctx.createMediaStreamSource(stream);

    // Tap the mic for the TX level meter BEFORE the worklet so the
    // meter reflects raw mic energy (downsampling can attenuate the
    // signal level slightly and confuse operators).
    const txAnalyser = this.ctx.createAnalyser();
    txAnalyser.fftSize = 256;
    txAnalyser.smoothingTimeConstant = 0.4;
    this.srcNode.connect(txAnalyser);
    this.txAnalyser = txAnalyser;

    const node = new AudioWorkletNode(this.ctx, "mcptt-tx", {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [1],
      processorOptions: {
        targetSampleRate: opts.sampleRate || 8000,
        frameSize,
      },
    });
    node.port.onmessage = (e) => {
      const m = e.data;
      if (!m || m.type !== "frame") return;
      const f: Float32Array = m.samples;
      // FleetConsole sends dynamic RTP payload type 106 for outbound
      // voice even when the payload bytes are µ-law. The manager
      // accepts this path; PT=0/8 is useful for inbound auto-detect
      // but does not match the legacy TX wire.
      const payloadType = PT_FLEET_VOICE;
      const payload = encodePcm(f, this.codec);
      const rtp = buildRtp(payload, this.txSeq, this.txTs, this.txSsrcOverride ?? randomSsrc(), payloadType);
      this.txSeq = (this.txSeq + 1) & 0xffff;
      this.txTs  = (this.txTs + frameSize) >>> 0;
      send(rtp);
    };
    this.srcNode.connect(node);
    // The worklet doesn't need to drive the destination, but Web Audio
    // graphs are ref-counted from the destination — we attach a muted
    // GainNode passthrough to keep the worklet alive.
    const sink = this.ctx.createGain();
    sink.gain.value = 0;
    node.connect(sink).connect(this.ctx.destination);
    this.txNode = node;
    this.log("audio TX started", { frameSize, codec: this.codec, sampleRate: opts.sampleRate });
  }

  stop(): void {
    try { this.txNode?.disconnect();     } catch { /* ignore */ }
    try { this.srcNode?.disconnect();    } catch { /* ignore */ }
    try { this.txAnalyser?.disconnect(); } catch { /* ignore */ }
    if (this.mediaStream) {
      for (const t of this.mediaStream.getTracks()) {
        try { t.stop(); } catch { /* ignore */ }
      }
    }
    this.mediaStream = null;
    this.srcNode = null;
    this.txNode = null;
    this.txAnalyser = null;
    this.log("audio TX stopped");
  }

  /** Dispose all resources (close mic, close context). Safe to call
   *  multiple times. */
  async close(): Promise<void> {
    this.stop();
    try { this.rxAnalyser?.disconnect(); } catch { /* ignore */ }
    try { this.rxGain?.disconnect();     } catch { /* ignore */ }
    try { this.rxNode?.disconnect();     } catch { /* ignore */ }
    this.rxNode = null;
    this.rxGain = null;
    this.rxAnalyser = null;
    if (this.ctx) {
      try { await this.ctx.close(); } catch { /* ignore */ }
      this.ctx = null;
    }
    this.debuggedFirstFrame = false;
  }

  // ============================================================
  // Volume / mute / level meter
  // ============================================================

  /** Set master playback volume in [0, 1]. Persists across mute. */
  setVolume(v: number): void {
    const clamped = Math.max(0, Math.min(1, v));
    this._volume = clamped;
    if (!this._muted && this.rxGain) {
      this.rxGain.gain.value = clamped;
    }
    if (clamped > 0 && this._muted) {
      // Operator nudged the slider — interpret as "un-mute".
      this.setMuted(false);
    }
  }
  getVolume(): number { return this._volume; }

  /** Mute master playback. Volume is preserved for un-mute. */
  setMuted(b: boolean): void {
    if (b === this._muted) return;
    if (b) {
      this._volumeBeforeMute = this._volume;
      this._muted = true;
      if (this.rxGain) this.rxGain.gain.value = 0;
    } else {
      this._muted = false;
      const v = this._volumeBeforeMute > 0 ? this._volumeBeforeMute : 1;
      this._volume = v;
      if (this.rxGain) this.rxGain.gain.value = v;
    }
  }
  isMuted(): boolean { return this._muted; }

  /** Live RX level in [0, 1] (RMS over the analyser window). Returns
   *  0 if audio isn't running yet. Cheap; safe to poll at 30Hz. */
  getRxLevel(): number { return this.readAnalyserRms(this.rxAnalyser); }
  /** Live TX (mic) level in [0, 1]. */
  getTxLevel(): number { return this.readAnalyserRms(this.txAnalyser); }

  private readAnalyserRms(a: AnalyserNode | null): number {
    if (!a) return 0;
    const buf = this.analyserScratch.length === a.fftSize
      ? this.analyserScratch
      : (this.analyserScratch = new Uint8Array(a.fftSize));
    a.getByteTimeDomainData(buf);
    // Time-domain bytes are unsigned: 128 = silence, 0 / 255 = peak.
    // RMS over the window, normalised to [0, 1].
    let sumSq = 0;
    for (let i = 0; i < buf.length; i++) {
      const v = (buf[i] - 128) / 128;
      sumSq += v * v;
    }
    const rms = Math.sqrt(sumSq / buf.length);
    // Light compression so quiet voice isn't a barely-visible nub —
    // sqrt curve is the standard meter trick.
    return Math.min(1, Math.sqrt(rms));
  }
}
